import { FieldCardType, FieldData, FieldName } from 'netvalve-types';
import * as Sentry from '@sentry/browser';
import { FieldConfig, InputStyles } from './types';

interface NetvalveElement extends HTMLElement {
    savedStyles: string;
}

export function findSDKScript(): HTMLScriptElement | null {
    const thisScript = document.currentScript as HTMLScriptElement;
    if (thisScript) return thisScript;
    const scripts = document.getElementsByTagName('script');
    if (import.meta.env?.VITE_APP_ENV !== 'local') return null;
    return Array.from(scripts).find((script) => {
        return script.type === 'module' && script.src && script.src.includes('index.ts');
    }) as HTMLScriptElement;
}

export function mergeStyleString(parentStyles: string, childStyles: string): string {
    // Convert style strings into objects
    const parseStyleString = (styleString: string) =>
        styleString
            ? Object.fromEntries(
                  styleString
                      .split(';')
                      .map((s) => {
                          const [key, value] = s.split(':').map((str) => str.trim());
                          return key ? [key, value] : null;
                      })
                      .filter(Boolean) as [string, string][]
              )
            : {};

    const parentStylesObj = parseStyleString(parentStyles);
    const childStylesObj = parseStyleString(childStyles);

    // Merge objects (child styles take precedence)
    const mergedStyles = { ...parentStylesObj, ...childStylesObj };

    // Convert merged object back to a style string
    return Object.entries(mergedStyles)
        .map(([key, value]) => `${key}: ${value}`)
        .join('; ');
}

export function mergeStyleObject(parentStyles: string, styleObject: InputStyles): string {
    // Convert InputStyles object to CSS string, handling quotes properly
    const styleString = Object.entries(styleObject)
        .map(([property, value]) => {
            // Remove any existing quotes from the value
            const cleanValue = String(value).replace(/['"]/g, '');
            return `${property}: ${cleanValue};`;
        })
        .join(' ');
    return mergeStyleString(parentStyles, styleString);
}

export type DetectedStylesOutput = {
    inputStyles: {
        [key: string]: string;
    };
    inputHeight: string;
};

type DetectStylesParams = {
    /** The element requesting style detection (usually 'this' from a web component) */
    sourceElement: Element;

    /** Styles directly applied to the element via style attribute */
    elementStyles: string;

    /** Styles that should be inherited from a parent element */
    inheritedStyles?: string;

    /** Styles that should override both element and inherited styles */
    overrideStyles?: string;

    /** Container element to search within for a reference input element.
     * If provided, will look for inputs inside this container.
     * If not provided, will look for inputs in the closest form to sourceElement.
     */
    referenceContainer?: HTMLElement;

    /** Defines the type of the styling applied */
    type?: string;
};

export function detectStyles({
    sourceElement,
    elementStyles = '',
    inheritedStyles = '',
    overrideStyles = '',
    referenceContainer,
    type,
}: DetectStylesParams): DetectedStylesOutput {
    // Find the nearest visible input element
    const nearestInput = referenceContainer
        ? referenceContainer.querySelector('input:not([type="hidden"]):not([style*="display: none"])')
        : sourceElement.closest('form')?.querySelector('input:not([type="hidden"]):not([style*="display: none"])');

    const input = document.createElement('input');

    // Copy classes from the nearest input if found
    if (nearestInput) {
        input.className = nearestInput.className;
        // Insert next to the reference input to inherit structural styles
        nearestInput.insertAdjacentElement('afterend', input);
    } else {
        // Fallback to original behavior
        sourceElement.insertAdjacentElement('afterend', input);
    }

    // Element styles should override inherited styles
    const baseStyles = inheritedStyles
        ? mergeStyleString(inheritedStyles, elementStyles) // elementStyles override inheritedStyles
        : elementStyles;

    // Then apply override styles on top if they exist
    const finalStyles = overrideStyles ? mergeStyleString(baseStyles, overrideStyles) : baseStyles;

    // Apply the merged styles
    input.setAttribute('style', finalStyles);

    // Extract styles
    const computedStyles = window.getComputedStyle(input);
    const inputStyles: { [key: string]: string } = {};

    // Determine if overrideStyles explicitly includes 'width' or 'position'
    const overrideKeys = new Set(overrideStyles);
    const overrideWidthExists = overrideKeys.has('width');
    const overridePositionExists = overrideKeys.has('position');

    for (const key of computedStyles) {
        const computedValue = computedStyles.getPropertyValue(key as string);

        if (key === 'width') {
            // Apply overridden width if it exists, otherwise default to 100%
            inputStyles[key as string] = overrideWidthExists ? computedValue : '100%';
        } else if (key === 'position') {
            // Apply overridden position if it exists, otherwise default to absolute
            // and ensure 'absolute' positioning doesn't apply to placeholders
            inputStyles[key as string] = overridePositionExists
                ? computedValue
                : type !== 'placeholder'
                  ? 'absolute'
                  : 'relative';
        } else {
            // Apply all other computed styles normally
            inputStyles[key as string] = computedValue;
        }
    }

    // Calculate height
    const calculatedHeight =
        input.offsetHeight + parseFloat(computedStyles.marginTop) + parseFloat(computedStyles.marginBottom);

    const MINIMUM_INPUT_HEIGHT = 47;
    const inputHeight = `${Math.max(calculatedHeight, MINIMUM_INPUT_HEIGHT)}px`;

    // Clean up
    input.parentNode!.removeChild(input);

    return { inputStyles, inputHeight };
}

export function getQueryParams(url: string) {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    return {
        paymentToken: params.get('paymentToken'),
        jwtToken: params.get('jwtToken'),
        correlationId: params.get('correlationId'),
    };
}

export function applyCommonAttributes(el: HTMLElement, config: FieldConfig) {
    const netvalveElement = el as unknown as NetvalveElement;
    if (config?.inputStyles) {
        Object.assign(el.style, config.inputStyles);
        netvalveElement.savedStyles = mergeStyleObject(
            netvalveElement.getAttribute('savedstyles') || '',
            config.inputStyles || {}
        );
    }

    if (config?.placeholder) netvalveElement.setAttribute('placeholder', config.placeholder);
}

export function createFieldData(params: {
    fieldName: FieldName;
    jwtToken: string;
    paymentToken: string;
    typeData?: FieldCardType;
    styles?: Record<string, string>;
    validate?: boolean;
    invalidStyles?: Record<string, string>;
    placeholderStyles?: Record<string, string>;
    showCardLogo?: boolean;
    font?: string;
    hidePlaceholder?: boolean;
    placeholder?: string;
}): FieldData {
    return {
        styles: params.styles ?? {},
        jwtToken: params.jwtToken,
        paymentToken: params.paymentToken,
        fieldName: params.fieldName,
        typeData: params?.typeData,
        validate: params?.validate,
        invalidStyles: params?.invalidStyles,
        placeholderStyles: params?.placeholderStyles,
        showCardLogo: params?.showCardLogo,
        font: params?.font,
        hidePlaceholder: params?.hidePlaceholder,
        placeholder: params?.placeholder,
    };
}

export function applyStyles(
    element: HTMLElement,
    styles: { [key: string]: string },
    isValidationStyles: boolean = false,
    isPlaceholderStyles: boolean = false
) {
    for (const [key, value] of Object.entries(styles)) {
        element.style.setProperty(key, value, isValidationStyles || isPlaceholderStyles ? 'important' : '');
    }
}

type SentryErrorParams = {
    name:
        | 'JS_INITIATE'
        | 'HTML_INITIATE'
        | 'TOKENIZE'
        | 'VALIDATE'
        | 'SUBMIT'
        | 'STYLE'
        | 'PENDING_ENCRYPTION_TIMEOUT'
        | 'MERCHANT_CALLBACK_ERROR';
    msg: string;
    error?: Error | null;
    tags?: Record<string, string>;
    extra?: Record<string, any>;
};

export function sentryLog({ name, msg, error, tags, extra }: SentryErrorParams) {
    console.error(msg, error);
    Sentry.withScope((scope) => {
        scope.setExtra('Custom Message', msg);
        scope.setTransactionName(name);

        if (tags) {
            Object.entries(tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }

        if (extra) {
            Object.entries(extra).forEach(([key, value]) => {
                scope.setExtra(key, value);
            });
        }

        Sentry.captureException(error);
    });
}

export function getFormElement(formSelector: string | null, container?: HTMLElement): HTMLFormElement {
    // use the provided selector, or search children for nearest form
    const el = formSelector ? document.querySelector(formSelector) : container?.querySelector('form');
    if (!el) throw new Error('Form element or form container element not found');
    // the element may be the container, not the form element
    if (el?.tagName.toLowerCase() === 'form') return el as HTMLFormElement;
    return el.querySelector('form') as HTMLFormElement;
}

export function wrapCallback(callback: (...args: any[]) => void, callbackName: 'onValidate' | 'onSubmitPayment') {
    return (...args: unknown[]) => {
        try {
            return callback(...args);
        } catch (error) {
            sentryLog({
                name: 'MERCHANT_CALLBACK_ERROR',
                msg: `Error in merchant's ${callbackName} callback`,
                error: error as Error,
            });
            // Don't rethrow the error to prevent it from bubbling up
            return null; // or appropriate fallback value
        }
    };
}
